import type { SuggestedPrompt } from './types';

export interface AssistantRouteContext {
  label: string;
  headline: string;
  description: string;
  capabilities: string[];
  placeholder: string;
}

function runtimeAction(
  actionId: string,
  text: string,
  intent: SuggestedPrompt['intent'],
  capability: string,
  description: string,
  executionMode: SuggestedPrompt['executionMode'] = 'brief_only',
  category: SuggestedPrompt['category'] = 'analyze',
): SuggestedPrompt {
  return {
    actionId,
    text,
    intent,
    capability,
    description,
    executionMode,
    launchMode: 'runtime',
    category,
  };
}

const ROUTE_PROMPTS: Record<string, SuggestedPrompt[]> = {
  '/overview': [
    runtimeAction('overview-triage-approvals', 'Triage pending approvals', 'agent_workflow', 'Triage', 'Review pending approvals and draft a safe action queue.', 'proposal_draft', 'control'),
    runtimeAction('overview-explain-alert', 'Explain high alert impact', 'monitoring_triage', 'Analyze', 'Explain high alert impact and affected systems.'),
    runtimeAction('overview-manage-kpis', 'Prepare KPI management changes', 'workspace_action', 'Draft', 'Draft KPI panel changes before opening the KPI manager.', 'proposal_draft', 'draft'),
    runtimeAction('overview-portfolio-risk', 'Find agents driving portfolio risk', 'portfolio_overview', 'Analyze', 'Identify agents and metrics driving portfolio risk.'),
  ],
  '/agents': [
    runtimeAction('agents-draft-supervised-agent', 'Draft a new supervised agent', 'agent_workflow', 'Draft', 'Prepare a supervised paper/sandbox agent draft.', 'proposal_draft', 'draft'),
    runtimeAction('agents-explain-proposal-gates', 'Explain selected proposal gates', 'agent_workflow', 'Analyze', 'Explain risk, policy, mandate, and OMS gate evidence.'),
    runtimeAction('agents-safe-policy-actions', 'Show safe actions for policy-gated work', 'agent_workflow', 'Control', 'List safe next actions while blocking live routing.', 'proposal_draft', 'control'),
    runtimeAction('agents-prepare-pause-plan', 'Prepare pause or halt checklist', 'agent_workflow', 'Control', 'Draft a governed pause/halt checklist without executing it.', 'proposal_draft', 'control'),
  ],
  '/agent-detail': [
    runtimeAction('agent-detail-mandate-review', 'Review this agent mandate', 'agent_workflow', 'Analyze', 'Review mandate limits, policy boundary, and open control questions.'),
    runtimeAction('agent-detail-proposal-gates', 'Explain this agent proposal gates', 'agent_workflow', 'Analyze', 'Explain current proposal risk, approval, OMS, and audit evidence.'),
    runtimeAction('agent-detail-audit-brief', 'Prepare agent audit brief', 'agent_workflow', 'Audit', 'Draft an audit-ready brief from this agent detail evidence.', 'proposal_draft', 'draft'),
    runtimeAction('agent-detail-pause-plan', 'Prepare pause or halt checklist', 'agent_workflow', 'Control', 'Draft a governed pause/halt checklist for this agent without executing it.', 'proposal_draft', 'control'),
  ],
  '/automations': [
    runtimeAction('automations-draft-supervised-agent', 'Draft a new supervised agent', 'agent_workflow', 'Draft', 'Prepare a supervised paper/sandbox agent draft.', 'proposal_draft', 'draft'),
    runtimeAction('automations-explain-proposal-gates', 'Explain selected proposal gates', 'agent_workflow', 'Analyze', 'Explain risk, policy, mandate, and OMS gate evidence.'),
    runtimeAction('automations-safe-policy-actions', 'Show safe actions for policy-gated work', 'agent_workflow', 'Control', 'List safe next actions while blocking live routing.', 'proposal_draft', 'control'),
  ],
  '/strategies': [
    runtimeAction('strategies-review-candidates', 'Review strategy candidates', 'strategy_action', 'Analyze', 'Rank strategy candidates by fit evidence for the current desk context.'),
    runtimeAction('strategies-agent-draft', 'Convert a strategy into an agent draft', 'strategy_action', 'Draft', 'Draft a supervised agent from a selected strategy.', 'proposal_draft', 'draft'),
    runtimeAction('strategies-compare-tradeoffs', 'Compare strategy tradeoffs', 'strategy_action', 'Analyze', 'Compare risk, market regime, and operational tradeoffs.'),
    runtimeAction('strategies-prepare-filters', 'Prepare strategy filters', 'workspace_action', 'Guide', 'Suggest filter settings for risk, market, level, and status.', 'brief_only', 'analyze'),
  ],
  '/strategy-detail': [
    runtimeAction('strategy-detail-backtest', 'Prepare backtest for this strategy', 'backtest_action', 'Simulate', 'Prepare a backtest configuration from this strategy detail page.', 'proposal_draft', 'simulate'),
    runtimeAction('strategy-detail-use-plan', 'Prepare use-strategy plan', 'strategy_action', 'Draft', 'Draft the safe setup steps before using this strategy.', 'proposal_draft', 'draft'),
    runtimeAction('strategy-detail-agent-draft', 'Draft agent from this strategy', 'strategy_action', 'Draft', 'Create a supervised agent draft from this strategy evidence.', 'proposal_draft', 'draft'),
    runtimeAction('strategy-detail-fit-review', 'Review strategy fit and risks', 'strategy_action', 'Analyze', 'Explain fit, advantages, considerations, parameters, and compatible strategies.'),
  ],
  '/connections': [
    runtimeAction('connections-audit-credentials', 'Audit credential posture', 'connection_audit', 'Audit', 'Audit venue, wallet, and credential readiness without exposing secrets.'),
    runtimeAction('connections-stale-fix', 'Prepare stale connection fix', 'connection_audit', 'Draft', 'Draft a remediation checklist for stale or degraded connections.', 'proposal_draft', 'draft'),
    runtimeAction('connections-agent-access', 'Check venue access for agents', 'connection_audit', 'Analyze', 'Check which agent actions can safely use connected venues.'),
    runtimeAction('connections-add-connection-plan', 'Prepare add-connection plan', 'connect_exchange', 'Draft', 'Prepare safe steps for adding an exchange, wallet, or venue.', 'proposal_draft', 'draft'),
  ],
  '/console': [
    runtimeAction('console-open-palette-plan', 'Prepare command-palette workflow', 'workspace_action', 'Guide', 'Prepare safe steps for staging a console command.', 'brief_only', 'control'),
    runtimeAction('console-parse-command', 'Parse console command safely', 'workspace_action', 'Draft', 'Draft the parsed command ticket without execution.', 'proposal_draft', 'draft'),
    runtimeAction('console-simulate-ticket', 'Simulate staged command ticket', 'workspace_action', 'Simulate', 'Prepare simulation-only command execution evidence.', 'proposal_draft', 'simulate'),
    runtimeAction('console-pretrade-guardrails', 'Explain pre-trade guardrails', 'risk_help', 'Analyze', 'Explain signal stack, venue matrix, and guardrail implications.'),
    runtimeAction('console-audit-trail', 'Summarize console audit trail', 'workspace_action', 'Audit', 'Summarize staged command and simulation audit evidence.'),
  ],
  '/compliance': [
    runtimeAction('compliance-alert-review', 'Prepare alert review', 'compliance_review', 'Review', 'Prepare a redacted compliance alert review packet.', 'proposal_draft', 'draft'),
    runtimeAction('compliance-ack-rationale', 'Draft acknowledgement rationale', 'compliance_review', 'Draft', 'Draft acknowledgement rationale for operator review.', 'proposal_draft', 'draft'),
    runtimeAction('compliance-audit-gaps', 'Summarize audit trail gaps', 'compliance_review', 'Audit', 'Summarize audit evidence gaps and safe next steps.'),
    runtimeAction('compliance-export-packet', 'Prepare evidence export packet', 'compliance_review', 'Export', 'Prepare export packet scope without marking alerts complete.', 'proposal_draft', 'export'),
  ],
  '/reports': [
    runtimeAction('reports-explain-pnl', 'Explain P&L drivers', 'report_analysis', 'Analyze', 'Explain P&L, attribution, and fee drivers.'),
    runtimeAction('reports-underperforming-agents', 'Find underperforming agents', 'report_analysis', 'Analyze', 'Identify agents that need review.'),
    runtimeAction('reports-ic-note', 'Draft investment committee note', 'report_analysis', 'Draft', 'Draft an investment committee note from report evidence.', 'proposal_draft', 'draft'),
    runtimeAction('reports-export-pack', 'Prepare report export pack', 'workspace_action', 'Export', 'Prepare performance, attribution, or risk export scope.', 'proposal_draft', 'export'),
  ],
  '/tax': [
    runtimeAction('tax-resolve-warnings', 'Resolve reconciliation warnings', 'tax_reconcile', 'Review', 'Review lot, FX, transfer, and taxable-event warnings.', 'proposal_draft', 'draft'),
    runtimeAction('tax-export-checklist', 'Prepare export checklist', 'tax_reconcile', 'Draft', 'Draft an export readiness checklist.', 'proposal_draft', 'export'),
    runtimeAction('tax-explain-events', 'Explain taxable events', 'tax_reconcile', 'Analyze', 'Explain taxable event categories and review state.'),
    runtimeAction('tax-sync-diagnostics', 'Prepare sync diagnostics', 'workspace_action', 'Analyze', 'Prepare diagnostics for tax sync and engine state.'),
  ],
  '/backtesting': [
    runtimeAction('backtesting-interpret-selected', 'Interpret selected backtests', 'backtest_action', 'Analyze', 'Interpret selected backtest results.'),
    runtimeAction('backtesting-draft-best-run', 'Draft agent from best run', 'backtest_action', 'Draft', 'Draft a supervised paper-mode agent from the best run.', 'proposal_draft', 'draft'),
    runtimeAction('backtesting-new-backtest', 'Configure a new backtest', 'backtest_action', 'Simulate', 'Prepare a new backtest configuration.', 'proposal_draft', 'simulate'),
    runtimeAction('backtesting-compare-selected', 'Compare selected backtests', 'backtest_action', 'Analyze', 'Compare selected strategies and risk metrics.'),
  ],
  '/monitoring': [
    runtimeAction('monitoring-triage-incidents', 'Triage live incidents', 'monitoring_triage', 'Triage', 'Triage incidents, alerts, and queues.', 'proposal_draft', 'control'),
    runtimeAction('monitoring-restart-checklist', 'Prepare restart checklist', 'monitoring_triage', 'Draft', 'Draft a restart checklist without executing controls.', 'proposal_draft', 'control'),
    runtimeAction('monitoring-health-sweep', 'Run agent health sweep', 'monitoring_triage', 'Analyze', 'Prepare a health sweep across agents and services.'),
    runtimeAction('monitoring-filter-critical', 'Focus critical unresolved alerts', 'workspace_action', 'Guide', 'Prepare filter criteria for critical unresolved alerts.'),
  ],
  '/tenants': [
    runtimeAction('tenants-create-plan', 'Prepare create-tenant plan', 'workspace_action', 'Draft', 'Draft tenant onboarding settings before opening create tenant.', 'proposal_draft', 'admin'),
    runtimeAction('tenants-export-summary', 'Prepare tenant export summary', 'workspace_action', 'Export', 'Prepare tenant export scope and evidence.', 'proposal_draft', 'export'),
    runtimeAction('tenants-kill-switch-plan', 'Prepare tenant kill-switch checklist', 'workspace_action', 'Control', 'Draft tenant kill-switch checks without executing them.', 'proposal_draft', 'control'),
    runtimeAction('tenants-sort-filter', 'Suggest tenant filters', 'workspace_action', 'Guide', 'Suggest tenant sort and region filters.'),
  ],
  '/tenants-new': [
    runtimeAction('tenant-create-submit-plan', 'Prepare tenant creation submission', 'workspace_action', 'Draft', 'Validate name, slug, and status before creating the tenant.', 'proposal_draft', 'admin'),
    runtimeAction('tenant-create-slug-check', 'Validate tenant slug and status', 'workspace_action', 'Analyze', 'Check tenant slug, status, and onboarding posture.'),
    runtimeAction('tenant-create-cancel-plan', 'Prepare cancel-create fallback', 'workspace_action', 'Guide', 'Prepare a safe return-to-tenants fallback without saving changes.'),
  ],
  '/tenant-detail': [
    runtimeAction('tenant-detail-review', 'Review tenant detail evidence', 'workspace_action', 'Analyze', 'Summarize tenant status, region, legal entity, and activity.'),
    runtimeAction('tenant-detail-management-plan', 'Prepare tenant management plan', 'workspace_action', 'Draft', 'Draft governed next steps for tenant management.', 'proposal_draft', 'admin'),
    runtimeAction('tenant-detail-export-evidence', 'Prepare tenant evidence export', 'workspace_action', 'Export', 'Prepare export scope for tenant details and memberships.', 'proposal_draft', 'export'),
    runtimeAction('tenant-detail-back-nav', 'Prepare return to tenant directory', 'workspace_action', 'Guide', 'Explain the safest navigation back to the tenant directory.'),
  ],
  '/admin': [
    runtimeAction('admin-add-user-plan', 'Prepare add-user plan', 'workspace_action', 'Draft', 'Draft user invite role and org assignment checks.', 'proposal_draft', 'admin'),
    runtimeAction('admin-refresh-health', 'Summarize system health refresh', 'workspace_action', 'Analyze', 'Summarize what to inspect after refreshing system health.'),
    runtimeAction('admin-rotate-keys', 'Prepare key rotation checklist', 'workspace_action', 'Control', 'Draft key rotation steps without touching secrets.', 'proposal_draft', 'control'),
    runtimeAction('admin-filter-logs', 'Prepare audit log filters', 'workspace_action', 'Audit', 'Suggest audit log filters for investigation.'),
  ],
  '/admin-strategy-library': [
    runtimeAction('admin-strategy-create-entry', 'Prepare new strategy entry', 'workspace_action', 'Draft', 'Draft catalog entry requirements before opening the create drawer.', 'proposal_draft', 'admin'),
    runtimeAction('admin-strategy-publish-plan', 'Prepare publish or draft change', 'workspace_action', 'Control', 'Draft status-change checks before publishing or reverting an entry.', 'proposal_draft', 'control'),
    runtimeAction('admin-strategy-archive-plan', 'Prepare archive or unarchive plan', 'workspace_action', 'Control', 'Draft archive and restore checks without changing catalog state.', 'proposal_draft', 'control'),
    runtimeAction('admin-strategy-filter-review', 'Review catalog filters', 'workspace_action', 'Analyze', 'Summarize category, overlay, status, and search filters.'),
  ],
  '/admin-user': [
    runtimeAction('admin-user-access-review', 'Review user access evidence', 'workspace_action', 'Audit', 'Summarize role, status, last login, and memberships.'),
    runtimeAction('admin-user-memberships', 'Summarize organization memberships', 'workspace_action', 'Analyze', 'Review organization memberships and status.'),
    runtimeAction('admin-user-role-change-plan', 'Prepare role-change checklist', 'workspace_action', 'Draft', 'Draft role and access change checks without mutating the user.', 'proposal_draft', 'admin'),
  ],
  '/settings': [
    runtimeAction('settings-risk-limits', 'Prepare risk-limit update', 'risk_help', 'Draft', 'Draft risk-limit changes for human review.', 'proposal_draft', 'draft'),
    runtimeAction('settings-notifications', 'Prepare notification settings', 'workspace_action', 'Draft', 'Draft notification routing preferences.', 'proposal_draft', 'draft'),
    runtimeAction('settings-security-review', 'Review workspace security settings', 'workspace_action', 'Audit', 'Review settings that affect security posture.'),
  ],
  '/notifications': [
    runtimeAction('notifications-explain', 'Explain this notification', 'explain_notification', 'Analyze', 'Explain notification meaning and impact.'),
    runtimeAction('notifications-triage', 'Triage notification queue', 'workspace_action', 'Triage', 'Group notifications into action buckets.', 'proposal_draft', 'control'),
    runtimeAction('notifications-clear-plan', 'Prepare notification cleanup plan', 'workspace_action', 'Draft', 'Draft cleanup steps without dismissing messages.', 'proposal_draft', 'draft'),
  ],
  '/profile': [
    runtimeAction('profile-edit-plan', 'Prepare profile edit plan', 'workspace_action', 'Draft', 'Draft profile field changes before saving them.', 'proposal_draft', 'draft'),
    runtimeAction('profile-notification-preferences', 'Prepare notification preference changes', 'workspace_action', 'Draft', 'Draft notification preference changes before applying them.', 'proposal_draft', 'draft'),
    runtimeAction('profile-revoke-session-plan', 'Prepare session revoke checklist', 'workspace_action', 'Control', 'Draft session revocation checks without ending sessions.', 'proposal_draft', 'control'),
    runtimeAction('profile-security-summary', 'Summarize account security posture', 'workspace_action', 'Audit', 'Summarize profile, notifications, and active-session posture.'),
  ],
  '/organization': [
    runtimeAction('organization-review-members', 'Review member access', 'workspace_action', 'Audit', 'Review org membership and access posture.'),
    runtimeAction('organization-portfolio-plan', 'Prepare portfolio scope change', 'workspace_action', 'Draft', 'Draft portfolio scope changes.', 'proposal_draft', 'draft'),
    runtimeAction('organization-invite-plan', 'Prepare invite checklist', 'workspace_action', 'Draft', 'Draft invite and role checks.', 'proposal_draft', 'admin'),
  ],
  '/api-keys': [
    runtimeAction('api-keys-create-plan', 'Prepare API key creation plan', 'workspace_action', 'Draft', 'Draft API key purpose, scope, and expiry checklist.', 'proposal_draft', 'admin'),
    runtimeAction('api-keys-rotate-plan', 'Prepare API key rotation plan', 'workspace_action', 'Control', 'Draft rotation steps without revealing secrets.', 'proposal_draft', 'control'),
    runtimeAction('api-keys-audit-scopes', 'Audit API key scopes', 'workspace_action', 'Audit', 'Review API key scope posture.'),
  ],
  '/status': [
    runtimeAction('status-diagnostics', 'Summarize system status', 'workspace_action', 'Analyze', 'Summarize service status and diagnostics.'),
    runtimeAction('status-incident-note', 'Draft incident note', 'monitoring_triage', 'Draft', 'Draft an incident note from current status.', 'proposal_draft', 'draft'),
    runtimeAction('status-refresh-plan', 'Prepare refresh checklist', 'workspace_action', 'Guide', 'Prepare a refresh and verification checklist.'),
  ],
  '/internal-checks': [
    runtimeAction('internal-checks-explain-failures', 'Explain failing internal checks', 'workspace_action', 'Analyze', 'Explain security, consent, CSP, HTTPS, and 404 check status.'),
    runtimeAction('internal-checks-remediation-plan', 'Prepare remediation checklist', 'workspace_action', 'Draft', 'Draft remediation steps for failing internal checks.', 'proposal_draft', 'draft'),
    runtimeAction('internal-checks-status-brief', 'Draft internal status brief', 'workspace_action', 'Draft', 'Prepare a support-ready internal checks brief.', 'proposal_draft', 'draft'),
  ],
  '/help': [
    { text: 'Getting started guide', intent: 'kb_search', capability: 'Guide', launchMode: 'chat', category: 'navigate' },
    { text: 'How to create my first trading agent', intent: 'bot_create_help', capability: 'Guide', launchMode: 'chat', category: 'navigate' },
    runtimeAction('help-support-brief', 'Prepare support brief', 'workspace_action', 'Draft', 'Draft a support brief from the current question.', 'proposal_draft', 'draft'),
  ],
};

const DEFAULT_PROMPTS: SuggestedPrompt[] = [
  runtimeAction('workspace-show-actions', 'Show available assistant actions', 'workspace_action', 'Guide', 'Show launchable AI actions for this workspace.'),
  runtimeAction('workspace-triage-desk', 'Triage my desk state', 'agent_workflow', 'Triage', 'Triage approvals, alerts, agents, and portfolio context.', 'proposal_draft', 'control'),
  runtimeAction('workspace-summarize-portfolio', 'Summarize portfolio context', 'portfolio_overview', 'Analyze', 'Summarize portfolio risk and performance context.'),
];

const ROUTE_CONTEXT: Record<string, AssistantRouteContext> = {
  '/overview': {
    label: 'Overview',
    headline: 'Desk command context',
    description: 'Triage approvals, alerts, live feed events, portfolio drift, and agent workload.',
    capabilities: ['Triage', 'Explain', 'Draft'],
    placeholder: 'Ask Rugira to triage approvals, alerts, or portfolio drift...',
  },
  '/connections': {
    label: 'Connections',
    headline: 'Connection posture context',
    description: 'Audit exchange, wallet, venue, and credential readiness before agents use them.',
    capabilities: ['Audit', 'Validate', 'Draft fixes'],
    placeholder: 'Ask Rugira to audit credentials, stale venues, or agent access...',
  },
  '/strategies': {
    label: 'Strategies',
    headline: 'Strategy selection context',
    description: 'Compare strategies, explain tradeoffs, and draft supervised agents from catalog entries.',
    capabilities: ['Review', 'Compare', 'Draft'],
    placeholder: 'Ask Rugira to compare strategies or draft an agent from one...',
  },
  '/strategy-detail': {
    label: 'Strategy Detail',
    headline: 'Strategy detail context',
    description: 'Backtest, use, or convert this strategy into a governed supervised-agent draft.',
    capabilities: ['Backtest', 'Use', 'Draft'],
    placeholder: 'Ask Rugira to backtest, use, or draft an agent from this strategy...',
  },
  '/backtesting': {
    label: 'Backtesting',
    headline: 'Backtest analysis context',
    description: 'Interpret selected runs, compare risk-adjusted results, and prepare agent drafts from validated tests.',
    capabilities: ['Interpret', 'Simulate', 'Draft'],
    placeholder: 'Ask Rugira to interpret results or draft an agent from a backtest...',
  },
  '/agents': {
    label: 'Agents',
    headline: 'Agent operating context',
    description: 'Launch drafts, explain proposals, inspect gates, and prepare safe next actions.',
    capabilities: ['Launch draft', 'Explain', 'Control'],
    placeholder: 'Ask Rugira to launch, inspect, or control supervised agents...',
  },
  '/agent-detail': {
    label: 'Agent Detail',
    headline: 'Agent evidence context',
    description: 'Review this agent mandate, proposal gates, audit trail, runtime evidence, and governed control options.',
    capabilities: ['Review', 'Audit', 'Control'],
    placeholder: 'Ask Rugira to review this agent mandate, proposal gates, or audit trail...',
  },
  '/console': {
    label: 'Trader Console',
    headline: 'Console command context',
    description: 'Stage, parse, and simulate console commands against signals, guardrails, and venue readiness.',
    capabilities: ['Parse', 'Simulate', 'Audit'],
    placeholder: 'Ask Rugira to parse, simulate, or audit console commands...',
  },
  '/monitoring': {
    label: 'Monitoring',
    headline: 'Incident response context',
    description: 'Triage system health, active alerts, queues, and restart checklists for affected agents.',
    capabilities: ['Triage', 'Diagnose', 'Checklist'],
    placeholder: 'Ask Rugira to triage incidents, queues, or service health...',
  },
  '/reports': {
    label: 'Analytics',
    headline: 'Performance analysis context',
    description: 'Explain P&L drivers, underperforming agents, risk metrics, and reporting packs.',
    capabilities: ['Analyze', 'Find drivers', 'Draft report'],
    placeholder: 'Ask Rugira to explain P&L, risk, or reporting drivers...',
  },
  '/tax': {
    label: 'Tax Center',
    headline: 'Tax reconciliation context',
    description: 'Review lot attribution, FX rates, taxable events, and export readiness.',
    capabilities: ['Reconcile', 'Review', 'Export prep'],
    placeholder: 'Ask Rugira to resolve warnings or prepare tax export evidence...',
  },
  '/compliance': {
    label: 'Compliance',
    headline: 'Compliance review context',
    description: 'Prepare alert reviews, acknowledgement rationale, audit summaries, and control evidence.',
    capabilities: ['Review', 'Audit', 'Draft'],
    placeholder: 'Ask Rugira to review compliance alerts or audit evidence...',
  },
  '/tenants': {
    label: 'Tenants',
    headline: 'Tenant operations context',
    description: 'Create tenants, switch workspace scope, export tenant evidence, and prepare kill-switch checks.',
    capabilities: ['Onboard', 'Export', 'Control'],
    placeholder: 'Ask Rugira to create, switch, export, or control tenant operations...',
  },
  '/tenants-new': {
    label: 'Create Tenant',
    headline: 'Tenant creation context',
    description: 'Validate tenant name, slug, and status before submitting or cancelling tenant creation.',
    capabilities: ['Validate', 'Create', 'Cancel'],
    placeholder: 'Ask Rugira to validate or prepare tenant creation...',
  },
  '/tenant-detail': {
    label: 'Tenant Detail',
    headline: 'Tenant evidence context',
    description: 'Review tenant metadata, activity, memberships, and governed management next steps.',
    capabilities: ['Review', 'Export', 'Manage'],
    placeholder: 'Ask Rugira to review or prepare tenant detail actions...',
  },
  '/admin': {
    label: 'Admin',
    headline: 'Administration context',
    description: 'Prepare user, system health, audit log, key rotation, and maintenance actions.',
    capabilities: ['Admin', 'Audit', 'Control'],
    placeholder: 'Ask Rugira to add users, inspect health, rotate keys, or filter logs...',
  },
  '/admin-strategy-library': {
    label: 'Strategy Catalog Admin',
    headline: 'Catalog administration context',
    description: 'Create, filter, publish, draft, archive, or restore strategy catalog entries with governed evidence.',
    capabilities: ['Create', 'Publish', 'Archive'],
    placeholder: 'Ask Rugira to create, publish, archive, or filter catalog entries...',
  },
  '/admin-user': {
    label: 'Admin User Detail',
    headline: 'User access context',
    description: 'Review user status, role, login activity, and organization memberships before access changes.',
    capabilities: ['Review', 'Audit', 'Draft'],
    placeholder: 'Ask Rugira to review user access or prepare role changes...',
  },
  '/settings': {
    label: 'Settings',
    headline: 'Settings governance context',
    description: 'Prepare risk, notification, security, and workspace setting changes before applying them.',
    capabilities: ['Configure', 'Review', 'Draft'],
    placeholder: 'Ask Rugira to prepare risk, notification, or security setting changes...',
  },
  '/notifications': {
    label: 'Notifications',
    headline: 'Notification triage context',
    description: 'Explain alerts, group notifications by urgency, and prepare cleanup plans.',
    capabilities: ['Explain', 'Triage', 'Draft'],
    placeholder: 'Ask Rugira to explain or triage notifications...',
  },
  '/profile': {
    label: 'Profile',
    headline: 'Account profile context',
    description: 'Prepare profile edits, notification preference changes, and active-session reviews.',
    capabilities: ['Edit', 'Notify', 'Secure'],
    placeholder: 'Ask Rugira to update profile details or review active sessions...',
  },
  '/organization': {
    label: 'Organization',
    headline: 'Organization access context',
    description: 'Review members, roles, portfolio scopes, and invite readiness.',
    capabilities: ['Audit', 'Invite', 'Scope'],
    placeholder: 'Ask Rugira to review members, roles, invites, or portfolio scopes...',
  },
  '/api-keys': {
    label: 'API Keys',
    headline: 'API key governance context',
    description: 'Prepare key creation, rotation, scope audits, and expiry reviews without exposing secrets.',
    capabilities: ['Scope', 'Rotate', 'Audit'],
    placeholder: 'Ask Rugira to prepare or audit API key actions...',
  },
  '/status': {
    label: 'Status',
    headline: 'System status context',
    description: 'Summarize service health, diagnostics, incident notes, and refresh checklists.',
    capabilities: ['Diagnose', 'Summarize', 'Draft'],
    placeholder: 'Ask Rugira to summarize status or draft incident notes...',
  },
  '/internal-checks': {
    label: 'Internal Checks',
    headline: 'Internal verification context',
    description: 'Explain internal security, consent, CSP, HTTPS, demo badge, and error-page checks.',
    capabilities: ['Diagnose', 'Explain', 'Draft'],
    placeholder: 'Ask Rugira to explain checks or draft remediation steps...',
  },
  '/help': {
    label: 'Help',
    headline: 'Help and support context',
    description: 'Find guidance, explain workflows, and prepare support briefs with relevant evidence.',
    capabilities: ['Guide', 'Explain', 'Draft'],
    placeholder: 'Ask Rugira for help or a support-ready brief...',
  },
};

const DEFAULT_CONTEXT: AssistantRouteContext = {
  label: 'Rugira',
  headline: 'Workspace context',
  description: 'Ask for governed analysis, drafts, and next actions across the trading desk.',
  capabilities: ['Guide', 'Analyze', 'Draft'],
  placeholder: 'Ask Rugira about agents, strategies, risk, or operations...',
};

function normalizeRoute(route: string) {
  const path = route.split('?')[0] || '/overview';
  if (path.startsWith('/agents/')) return '/agent-detail';
  if (path.startsWith('/agents')) return '/agents';
  if (path.startsWith('/automations') || path.startsWith('/bots') || path.startsWith('/trading')) return '/agents';
  if (path.startsWith('/strategies/')) return '/strategy-detail';
  if (path.startsWith('/strategies')) return '/strategies';
  if (path.startsWith('/console')) return '/console';
  if (path.startsWith('/connections') || path.startsWith('/venues') || path.startsWith('/exchanges')) return '/connections';
  if (path.startsWith('/reports')) return '/reports';
  if (path.startsWith('/tax')) return '/tax';
  if (path.startsWith('/tenants/new')) return '/tenants-new';
  if (path.startsWith('/tenants/')) return '/tenant-detail';
  if (path.startsWith('/tenants')) return '/tenants';
  if (path.startsWith('/admin/strategy-library')) return '/admin-strategy-library';
  if (path.startsWith('/admin/user/')) return '/admin-user';
  if (path.startsWith('/admin')) return '/admin';
  if (path.startsWith('/api-keys')) return '/api-keys';
  if (path.startsWith('/organization')) return '/organization';
  if (path.startsWith('/settings')) return '/settings';
  if (path.startsWith('/notifications')) return '/notifications';
  if (path.startsWith('/profile')) return '/profile';
  if (path.startsWith('/internal/checks')) return '/internal-checks';
  if (path.startsWith('/status')) return '/status';
  if (path.startsWith('/help')) return '/help';
  return path;
}

export function getSuggestedPrompts(route: string): SuggestedPrompt[] {
  return ROUTE_PROMPTS[normalizeRoute(route)] ?? DEFAULT_PROMPTS;
}

export function getAssistantRouteContext(route: string): AssistantRouteContext {
  return ROUTE_CONTEXT[normalizeRoute(route)] ?? DEFAULT_CONTEXT;
}
